if (!customElements.get('build-your-stack')) {
  class BuildYourStack extends HTMLElement {
    constructor() {
      super();
      this.selected = new Map();
      this.purchaseType = 'subscribe';
      this.bundleDiscount = Number(this.dataset.bundleDiscount || 0);
      this.bundleMinItems = Number(this.dataset.bundleMinItems || 2);
      this.subscriptionDiscount = Number(this.dataset.subscriptionDiscount || 0);
      this.minItems = Number(this.dataset.minItems || 1);
      this.enableSubscriptions = this.dataset.enableSubscriptions === 'true';
    }

    connectedCallback() {
      this.products = this.#readProducts();
      this.#cacheElements();
      this.#bindEvents();
      this.#syncDeliveryUI();
      this.#updateVariantSelects();
      this.#preselectAllProducts();
      this.#updateProductCardPrices();
      this.#renderSummary();
    }

    #readProducts() {
      const node = this.querySelector('[data-build-your-stack-products]');
      if (!node) return [];
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.error('Build your stack: invalid product JSON', error);
        return [];
      }
    }

    #cacheElements() {
      this.summaryItemsEl = this.querySelector('[data-summary-items]');
      this.subtotalEl = this.querySelector('[data-summary-subtotal]');
      this.bundleDiscountEl = this.querySelector('[data-summary-bundle-discount]');
      this.bundleDiscountRow = this.querySelector('[data-summary-bundle-row]');
      this.subscriptionDiscountEl = this.querySelector('[data-summary-subscription-discount]');
      this.subscriptionDiscountRow = this.querySelector('[data-summary-subscription-row]');
      this.totalEl = this.querySelector('[data-summary-total]');
      this.totalLabelEl = this.querySelector('[data-summary-total-label]');
      this.ctaEl = this.querySelector('[data-stack-cta]');
      this.ctaLabelEl = this.querySelector('[data-stack-cta-label]');
      this.emptyMessage = this.dataset.emptyMessage || 'Select products to build your stack.';
      this.subscribeCtaLabel = this.dataset.subscribeCtaLabel || 'Start subscription';
      this.oneTimeCtaLabel = this.dataset.oneTimeCtaLabel || 'Add stack to cart';
    }

    #bindEvents() {
      this.querySelectorAll('[data-product-row]').forEach((row) => {
        row.addEventListener('click', (event) => {
          if (event.target.closest('[data-variant-select]')) return;
          this.#toggleProduct(row);
        });

        row.addEventListener('keydown', (event) => {
          if (event.target.closest('[data-variant-select]')) return;
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this.#toggleProduct(row);
        });
      });

      this.querySelectorAll('[data-variant-select]').forEach((select) => {
        select.addEventListener('click', (event) => event.stopPropagation());
        select.addEventListener('change', (event) => {
          event.stopPropagation();
          this.#changeVariant(event.target);
          this.#updateProductCardPrices();
        });
      });

      this.querySelectorAll('[data-delivery-option]').forEach((button) => {
        button.addEventListener('click', () => this.#setPurchaseType(button.dataset.deliveryOption));
      });

      this.ctaEl?.addEventListener('click', () => this.#addToCart());
    }

    #productKey(productId) {
      return String(productId);
    }

    #isVariantSelectable(variant, product) {
      if (!variant) return false;

      if (this.purchaseType === 'one-time') {
        if (variant.available) return true;
        if (product?.available && !product?.requires_selling_plan) return true;
        return false;
      }

      if (variant.available) return true;

      return (
        this.enableSubscriptions &&
        Array.isArray(variant.selling_plan_allocations) &&
        variant.selling_plan_allocations.length > 0
      );
    }

    #getDefaultVariant(product) {
      const selectable = product.variants.filter((variant) => this.#isVariantSelectable(variant, product));
      return selectable[0] || product.variants[0];
    }

    #getVariantBasePrice(variant) {
      return Number(variant.price);
    }

    #getVariantPrice(variant) {
      return this.#getVariantBasePrice(variant);
    }

    #getSellingPlanId(variant) {
      if (!this.enableSubscriptions || this.purchaseType !== 'subscribe') return null;
      const allocation = variant.selling_plan_allocations?.[0];
      return allocation ? allocation.selling_plan_id : null;
    }

    #getSelectedVariant(product, row) {
      const select = row?.querySelector('[data-variant-select]');
      const defaultVariant = this.#getDefaultVariant(product);
      const variantId = select ? Number(select.value) : defaultVariant?.id;
      return product.variants.find((variant) => Number(variant.id) === Number(variantId)) || defaultVariant;
    }

    #toggleProduct(row) {
      if (!row?.matches('[data-product-row]')) return;
      if (row.dataset.productPurchasable === 'false') return;

      const productId = row.dataset.productId;
      const product = this.products.find((item) => String(item.id) === String(productId));
      if (!product) return;

      const key = this.#productKey(productId);

      if (this.selected.has(key)) {
        this.selected.delete(key);
        row.classList.remove('is-selected');
        row.setAttribute('aria-pressed', 'false');
      } else {
        const variant = this.#getSelectedVariant(product, row);
        if (!variant || !this.#isVariantSelectable(variant, product)) return;

        this.selected.set(key, {
          productId: product.id,
          title: product.title,
          variantId: variant.id,
          price: this.#getVariantPrice(variant),
          sellingPlanId: this.#getSellingPlanId(variant),
        });
        row.classList.add('is-selected');
        row.setAttribute('aria-pressed', 'true');
      }

      this.#renderSummary();
    }

    #changeVariant(select) {
      const productId = select.dataset.productId;
      const key = this.#productKey(productId);
      if (!this.selected.has(key)) return;

      const product = this.products.find((item) => String(item.id) === String(productId));
      const variant = product?.variants.find((item) => Number(item.id) === Number(select.value));
      if (!product || !variant || !this.#isVariantSelectable(variant, product)) return;

      this.selected.set(key, {
        productId: product.id,
        title: product.title,
        variantId: variant.id,
        price: this.#getVariantPrice(variant),
        sellingPlanId: this.#getSellingPlanId(variant),
      });

      this.#renderSummary();
    }

    #setPurchaseType(type) {
      this.purchaseType = type === 'one-time' ? 'one-time' : 'subscribe';
      this.#syncDeliveryUI();
      this.#updateVariantSelects();
      this.#pruneInvalidSelections();
      this.#updateProductCardPrices();
      this.#renderSummary();
    }

    #updateVariantSelects() {
      this.querySelectorAll('[data-variant-select]').forEach((select) => {
        const productId = select.dataset.productId;
        const product = this.products.find((item) => String(item.id) === String(productId));
        if (!product) return;

        const currentValue = Number(select.value);
        let hasSelectable = false;

        select.querySelectorAll('option').forEach((option) => {
          const variant = product.variants.find((item) => Number(item.id) === Number(option.value));
          const selectable = this.#isVariantSelectable(variant, product);
          option.hidden = !selectable;
          option.disabled = !selectable;
          if (selectable) hasSelectable = true;
        });

        const currentVariant = product.variants.find((item) => Number(item.id) === currentValue);
        if (!this.#isVariantSelectable(currentVariant, product)) {
          const fallback = this.#getDefaultVariant(product);
          if (fallback) select.value = String(fallback.id);
        }

        select.disabled = !hasSelectable;
      });
    }

    #updateProductCardPrices() {
      this.querySelectorAll('[data-product-price]').forEach((priceEl) => {
        const productId = priceEl.dataset.productId;
        const product = this.products.find((item) => String(item.id) === String(productId));
        if (!product) return;

        const row = priceEl.closest('[data-product-row]');
        const variant = this.#getSelectedVariant(product, row);
        if (!variant) return;

        priceEl.textContent = this.#formatMoney(this.#getVariantBasePrice(variant));
      });
    }

    #pruneInvalidSelections() {
      for (const [key, item] of [...this.selected.entries()]) {
        const product = this.products.find((entry) => String(entry.id) === String(item.productId));
        const variant = product?.variants.find((entry) => Number(entry.id) === Number(item.variantId));
        if (!product || !this.#isVariantSelectable(variant, product)) {
          this.selected.delete(key);
          const row = this.querySelector(`[data-product-row][data-product-id="${item.productId}"]`);
          row?.classList.remove('is-selected');
          row?.setAttribute('aria-pressed', 'false');
        } else {
          this.selected.set(key, {
            ...item,
            price: this.#getVariantPrice(variant),
            sellingPlanId: this.#getSellingPlanId(variant),
          });
        }
      }
    }

    #preselectAllProducts() {
      if (this.dataset.preselectAll === 'false') return;

      this.querySelectorAll('[data-product-row]').forEach((row) => {
        if (row.dataset.productPurchasable === 'false') return;

        const product = this.products.find((item) => String(item.id) === String(row.dataset.productId));
        if (!product) return;

        const variant = this.#getSelectedVariant(product, row);
        if (!variant || !this.#isVariantSelectable(variant, product)) return;

        const key = this.#productKey(product.id);
        this.selected.set(key, {
          productId: product.id,
          title: product.title,
          variantId: variant.id,
          price: this.#getVariantPrice(variant),
          sellingPlanId: this.#getSellingPlanId(variant),
        });

        row.classList.add('is-selected');
        row.setAttribute('aria-pressed', 'true');
      });
    }

    #syncDeliveryUI() {
      this.querySelectorAll('[data-delivery-option]').forEach((button) => {
        const isActive = button.dataset.deliveryOption === this.purchaseType;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      if (this.ctaLabelEl) {
        this.ctaLabelEl.textContent =
          this.purchaseType === 'subscribe' ? this.subscribeCtaLabel : this.oneTimeCtaLabel;
      }

      if (this.totalLabelEl) {
        this.totalLabelEl.textContent =
          this.purchaseType === 'subscribe'
            ? this.dataset.totalSubscribeLabel || 'Total / month'
            : this.dataset.totalOneTimeLabel || 'Total';
      }
    }

    #formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, window.theme?.moneyFormat || Shopify.money_format);
      }
      return `$${(cents / 100).toFixed(2)}`;
    }

    #renderSummary() {
      const items = [...this.selected.values()];
      const subtotal = items.reduce((sum, item) => sum + item.price, 0);
      const bundleDiscountApplies = items.length >= this.bundleMinItems && this.bundleDiscount > 0;
      const bundleDiscountAmount = bundleDiscountApplies
        ? Math.round(subtotal * (this.bundleDiscount / 100))
        : 0;
      const afterBundle = subtotal - bundleDiscountAmount;
      const subscriptionDiscountAmount =
        this.purchaseType === 'subscribe'
          ? Math.round(afterBundle * (this.subscriptionDiscount / 100))
          : 0;
      const total = afterBundle - subscriptionDiscountAmount;

      if (this.summaryItemsEl) {
        if (!items.length) {
          this.summaryItemsEl.innerHTML = `<p class="build-your-stack__summary-empty">${this.emptyMessage}</p>`;
        } else {
          this.summaryItemsEl.innerHTML = items
            .map(
              (item) => `
                <div class="build-your-stack__summary-item">
                  <span>${item.title}</span>
                  <span>${this.#formatMoney(item.price)}</span>
                </div>
              `
            )
            .join('');
        }
      }

      if (this.subtotalEl) this.subtotalEl.textContent = this.#formatMoney(subtotal);
      if (this.bundleDiscountEl) {
        this.bundleDiscountEl.textContent = `-${this.#formatMoney(bundleDiscountAmount)}`;
      }
      if (this.bundleDiscountRow) {
        this.bundleDiscountRow.hidden = !bundleDiscountApplies;
      }
      if (this.subscriptionDiscountEl) {
        this.subscriptionDiscountEl.textContent = `-${this.#formatMoney(subscriptionDiscountAmount)}`;
      }
      if (this.subscriptionDiscountRow) {
        this.subscriptionDiscountRow.hidden =
          this.purchaseType !== 'subscribe' || this.subscriptionDiscount <= 0 || !items.length;
      }
      if (this.totalEl) this.totalEl.textContent = this.#formatMoney(Math.max(total, 0));

      if (this.ctaEl) {
        this.ctaEl.disabled = items.length < this.minItems;
      }
    }

    async #addToCart() {
      const items = [...this.selected.values()];
      if (items.length < this.minItems || !this.ctaEl || this.ctaEl.classList.contains('is-loading')) {
        return;
      }

      if (this.purchaseType === 'subscribe') {
        const missingPlan = items.some((item) => !item.sellingPlanId);
        if (missingPlan) {
          alert('One or more products do not have a subscription plan configured.');
          return;
        }
      }

      this.ctaEl.classList.add('is-loading');
      this.ctaEl.disabled = true;

      const payload = {
        items: items.map((item) => {
          const line = { id: item.variantId, quantity: 1 };
          if (this.purchaseType === 'subscribe' && item.sellingPlanId) {
            line.selling_plan = item.sellingPlanId;
          }
          return line;
        }),
      };

      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');
      const cartObject = cartDrawer || cartNotification;

      if (cartObject && typeof cartObject.getSectionsToRender === 'function') {
        payload.sections = cartObject.getSectionsToRender().map((section) => section.id);
        payload.sections_url = window.location.pathname;
      }

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();
        if (!response.ok) throw data;

        if (cartObject && typeof cartObject.renderContents === 'function') {
          cartObject.classList.remove('is-empty');
          cartObject.renderContents(data);
        } else {
          window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
        }
      } catch (error) {
        const message = error?.description || error?.message || 'Unable to add stack to cart.';
        alert(message);
      } finally {
        this.ctaEl.classList.remove('is-loading');
        this.#renderSummary();
      }
    }
  }

  customElements.define('build-your-stack', BuildYourStack);
}
